/**
 * App Configuration Utility
 * Determines the app name based on the current domain
 */

import { merchantOriginFromBuyerHostname } from '@vaiu/utils';

/**
 * Get the app name based on the current domain
 * This is used to fetch the correct configuration from the backend
 */
export const getAppNameForDomain = (): string => {
  const hostname = window.location.hostname;
  
  // Map domains to app names
  if (hostname.includes('vaiupay.com')) {
    return 'vaiupay-app';
  }
  
  if (hostname.includes('atomicmarketplace.com')) {
    return 'atomic-marketplace';
  }
  
  if (hostname.includes('rbko.')) {
    return 'rbko-app';
  }
  
  if (hostname.includes('bnp.banks.vaiu.app')) {
    return 'bnp-app';
  }

  if (hostname.includes('ing.banks.vaiu.app')) {
    return 'ing-app';
  }
  
  if (hostname.includes('payformyfood.')) {
    return 'payformyfood-app';
  }
  
  // Default to vaiu-app for vaiu.app and localhost
  return import.meta.env.VITE_APP_NAME || 'vaiu-app';
};

/**
 * Get display name for the app
 */
export const getAppDisplayName = (): string => {
  const hostname = window.location.hostname;
  
  if (hostname.includes('vaiupay.com')) {
    return 'VaiuPay';
  }
  
  if (hostname.includes('atomicmarketplace.com')) {
    return 'Atomic Marketplace';
  }
  
  if (hostname.includes('rbko.')) {
    return 'RBKo';
  }
  
  if (hostname.includes('bnp.banks.vaiu.app')) {
    return 'BNP Paribas';
  }

  if (hostname.includes('ing.banks.vaiu.app')) {
    return 'ING';
  }
  
  if (hostname.includes('payformyfood.')) {
    return 'Pay For My Food';
  }
  
  return 'Vaiu';
};

/**
 * Get the canonical buyer-facing payment app base URL.
 * Merchant tools may run on sell.vaiu.app, but customer payment links should
 * resolve to the core vaiu.app pay flow in production.
 */
export const getPaymentAppBaseUrl = (): string => {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return window.location.origin;
  }

  const envUrl = import.meta.env.VITE_PAYMENT_APP_URL?.trim();
  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }

  return 'https://vaiu.app';
};

/**
 * Merchant app origin. Onboarding and merchant tools live only on the merchant app.
 */
export const getMerchantAppOrigin = (): string => {
  if (typeof window === 'undefined') {
    return 'https://sell.vaiu.app';
  }
  const hostname = window.location.hostname.toLowerCase();
  const envUrl = import.meta.env.VITE_MERCHANT_APP_URL?.trim();
  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8082';
  }
  return merchantOriginFromBuyerHostname(hostname);
};
